import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import type { ColumnDef } from '@org/ui';
import {
  Badge,
  Button,
  Input,
  Switch,
  Table,
  Skeleton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@org/ui';
import { MoreHorizontal } from 'lucide-react';
import { useTeacherId } from '../../components/teacher-picker';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/feedback/')({
  component: FeedbackDashboardPage,
});

type FeedbackRow = {
  studentId: string;
  firstName: string | null;
  lastName: string | null;
  levelName: string | null;
  latestFeedback: {
    feedbackId: string;
    type: 'midterm' | 'final';
    dateFrom: Date | string;
    dateTo: Date | string;
    status: string;
  } | null;
  lastFeedbackReceivedAt: Date | string | null;
  feedbackDue: boolean;
};

function formatDate(date: Date | string | null): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('pt-BR');
}

function FeedbackDashboardPage() {
  const { t } = useTranslation('admin');
  const teacherId = useTeacherId();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [onlyDue, setOnlyDue] = useState(false);

  const { data, isLoading } = trpc.teacher.feedback.listAll.useQuery({ teacherId });
  const dueCount = useMemo(() => (data ?? []).filter((row) => row.feedbackDue).length, [data]);
  const rows = useMemo(() => (onlyDue ? (data ?? []).filter((row) => row.feedbackDue) : data ?? []), [data, onlyDue]);

  const columns: ColumnDef<FeedbackRow, unknown>[] = [
    {
      accessorFn: (row) => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim(),
      id: 'studentName',
      header: t('teacher.feedbackDashboard.columns.student'),
      cell: ({ row }) => {
        const name = `${row.original.firstName ?? ''} ${row.original.lastName ?? ''}`.trim();
        return <span className="font-medium text-gray-900">{name || t('teacher.feedbackDashboard.columns.noName')}</span>;
      },
    },
    {
      accessorKey: 'levelName',
      header: t('teacher.feedbackDashboard.columns.level'),
      cell: ({ row }) => (
        <span className="text-sm text-gray-600">{row.original.levelName ?? '-'}</span>
      ),
    },
    {
      id: 'lastFeedbackRange',
      header: t('teacher.feedbackDashboard.columns.lastPeriod'),
      cell: ({ row }) => {
        const fb = row.original.latestFeedback;
        if (!fb) return <span className="text-sm text-gray-400">-</span>;
        return (
          <span className="text-sm text-gray-600">
            {formatDate(fb.dateFrom)} - {formatDate(fb.dateTo)}
          </span>
        );
      },
    },
    {
      accessorFn: (row) => (row.lastFeedbackReceivedAt ? new Date(row.lastFeedbackReceivedAt).getTime() : 0),
      id: 'lastFeedbackReceived',
      header: t('teacher.feedbackDashboard.columns.lastReceived'),
      cell: ({ row }) => {
        const { lastFeedbackReceivedAt, feedbackDue } = row.original;
        return (
          <span className={`text-sm ${feedbackDue ? 'font-semibold text-[#cf2e2e]' : 'text-gray-700'}`}>
            {lastFeedbackReceivedAt
              ? formatDate(lastFeedbackReceivedAt)
              : t('teacher.feedbackDashboard.columns.neverReceived')}
          </span>
        );
      },
    },
    {
      id: 'lastFeedbackStatus',
      header: t('teacher.feedbackDashboard.columns.status'),
      cell: ({ row }) => {
        const fb = row.original.latestFeedback;
        if (!fb) return <Badge className="bg-gray-50 text-gray-400 text-xs">{t('teacher.feedbackDashboard.columns.noFeedback')}</Badge>;
        const statusKey = fb.status as 'pending' | 'generating' | 'generated' | 'published';
        const classNames: Record<string, string> = {
          pending: 'bg-gray-100 text-gray-600',
          generating: 'bg-yellow-100 text-yellow-700',
          generated: 'bg-blue-100 text-blue-700',
          published: 'bg-emerald-100 text-emerald-700',
        };
        return (
          <Badge className={`text-xs ${classNames[statusKey] ?? classNames.pending}`}>
            {t(`teacher.feedbackDashboard.statusLabels.${statusKey}`, t('teacher.feedbackDashboard.statusLabels.pending'))}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => <ActionsCell row={row.original} />,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{t('teacher.feedbackDashboard.eyebrow')}</p>
          <h1 className="text-3xl font-semibold text-gray-900">{t('teacher.feedbackDashboard.title')}</h1>
          <p className="text-sm text-gray-600">
            {t('teacher.feedbackDashboard.description')}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Input
          type="text"
          placeholder={t('teacher.feedbackDashboard.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm rounded-lg border-gray-200 bg-white px-4 focus-visible:ring-[#cf2e2e]"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <Switch checked={onlyDue} onCheckedChange={setOnlyDue} className="data-[state=checked]:bg-[#cf2e2e]" />
          {t('teacher.feedbackDashboard.filterDue')}
        </label>
        {!isLoading && (
          <Badge className={dueCount > 0 ? 'bg-[#cf2e2e] text-white' : 'bg-emerald-100 text-emerald-700'}>
            {t('teacher.feedbackDashboard.dueBadge', { count: dueCount })}
          </Badge>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && (!data || data.length === 0) && (
        <Card className="border border-dashed border-rose-200 bg-white/80">
          <CardHeader>
            <CardTitle>{t('teacher.feedbackDashboard.emptyTitle')}</CardTitle>
            <CardDescription>
              {t('teacher.feedbackDashboard.emptyDescription')}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && data && data.length > 0 && rows.length === 0 && (
        <Card className="border border-dashed border-emerald-200 bg-white/80">
          <CardHeader>
            <CardTitle>{t('teacher.feedbackDashboard.noMatchTitle')}</CardTitle>
            <CardDescription>{t('teacher.feedbackDashboard.noMatchDescription')}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && rows.length > 0 && (
        <Table
          tableData={rows}
          columns={columns}
          getRowId={(row) => row.studentId}
          globalFilter={search}
          onRowClick={(row) =>
            navigate({
              to: '/students/$studentId/feedback',
              params: { studentId: row.studentId },
            })
          }
        />
      )}
    </div>
  );
}

function ActionsCell({ row }: { row: FeedbackRow }) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => e.stopPropagation()}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem
          onClick={() =>
            navigate({
              to: '/students/$studentId/feedback',
              params: { studentId: row.studentId },
            })
          }
        >
          {t('teacher.feedbackDashboard.actions.viewFeedbacks')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() =>
            navigate({
              to: '/students/$studentId/feedback',
              params: { studentId: row.studentId },
              search: { newType: 'midterm' },
            })
          }
        >
          {t('teacher.feedbackDashboard.actions.newMidtermFeedback')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            navigate({
              to: '/students/$studentId/feedback',
              params: { studentId: row.studentId },
              search: { newType: 'final' },
            })
          }
        >
          {t('teacher.feedbackDashboard.actions.newFinalFeedback')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
